import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Icon } from '@iconify/react';

export default function ClearSiteDataDialog({ dialogOpen, okAction, cancelAction, site }) {
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!dialogOpen) {
      setConfirmText('');
      setError('');
      setLoading(false);
    }
  }, [dialogOpen]);

  const handlePaste = (e) => {
    e.preventDefault();
    return false;
  };

  const handleConfirm = async () => {
    if (confirmText !== site.name) {
      setError('Site name does not match. Please type the exact site name.');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await okAction();
    } catch (err) {
      setError('Failed to clear site data. Please try again.');
      setLoading(false);
    }
  };

  const isConfirmDisabled = confirmText !== site.name || loading;

  return (
    <Dialog 
      open={dialogOpen} 
      onClose={loading ? undefined : cancelAction}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon icon="mdi:alert-circle-outline" width="24" height="24" color="error" />
          <Typography variant="h6" component="span">
            Administrative Site Settings
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              ⚠️ DANGER: This action cannot be undone!
            </Typography>
            <Typography variant="body2">
              This will permanently delete ALL data associated with this site including:
            </Typography>
            <ul style={{ marginTop: '8px', marginBottom: '8px' }}>
              <li>Events</li>
              <li>Profile Activities</li>
              <li>Profile Change History (Audit Trail)</li>
              <li>Profile State Tracking</li>
              <li>Profile Activity Anomalies</li>
              <li>Doors</li>
              <li>Door Groups</li>
              <li>Profiles</li>
              <li>Access Levels</li>
            </ul>
          </Alert>

          <Typography variant="body1" sx={{ mb: 2 }}>
            You are about to clear all data for site: <strong>{site?.name}</strong>
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            To confirm this action, please type the exact site name below:
          </Typography>

          <TextField
            fullWidth
            label="Type site name to confirm"
            variant="outlined"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            onPaste={handlePaste}
            disabled={loading}
            error={!!error}
            helperText={error || 'Pasting is disabled. You must type the site name manually.'}
            autoComplete="off"
            sx={{ mb: 2 }}
          />

          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Clearing site data... This may take a moment.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={cancelAction} 
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
          variant="contained"
          color="error"
          startIcon={loading ? null : <Icon icon="mdi:delete-forever" />}
        >
          {loading ? 'Clearing...' : 'Clear Site Data'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ClearSiteDataDialog.propTypes = {
  dialogOpen: PropTypes.bool.isRequired,
  okAction: PropTypes.func.isRequired,
  cancelAction: PropTypes.func.isRequired,
  site: PropTypes.object.isRequired,
};


